import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { Period } from '@/lib/timePeriod';
import type { OffenderVehiclesResponse } from '@/types/offenders';

export interface UseOffenderVehiclesOptions {
  period: Period;
  /**
   * Default 100 — the Offender Vehicles watchlist fetches the whole page in
   * ONE request and sorts client-side, so this doubles as the grid's row
   * budget. Keep it modest; there is no server-side paging on this route.
   */
  limit?: number;
  /** Default false — Watch alerts are DQ noise; investigators rarely want them. */
  includeWatch?: boolean;
}

/**
 * Fetches the Offender Vehicles watchlist — vehicles ranked by alert volume
 * then risk for the selected period, with the per-severity split and
 * open-alert counts the grid colour-codes on.
 *
 * Backs `/app/offenders`. Distinct from `useTopOffenders`, which serves the
 * compact multi-entity Top Offenders panel on the Fraud Hub: this route is
 * vehicle-only and returns the richer per-row breakdown plus the server-side
 * `total_flagged` population count.
 *
 * Cache key includes every input so toggling period / limit / watch each flip
 * to a fresh query and don't cross-contaminate.
 */
export function useOffenderVehicles({
  period,
  limit = 100,
  includeWatch = false,
}: UseOffenderVehiclesOptions): UseQueryResult<OffenderVehiclesResponse, Error> {
  return useQuery<OffenderVehiclesResponse, Error>({
    queryKey: ['offender-vehicles', period, limit, includeWatch],
    queryFn: async () => {
      const { data } = await api.get<OffenderVehiclesResponse>(
        '/offenders/vehicles',
        {
          params: {
            period,
            limit,
            include_watch: includeWatch,
          },
        },
      );
      return data;
    },
    staleTime: 30_000,
  });
}
